<template>
  <base-form
    :aria-labelledby="id"
    :class="brandClasses.form"
    :form
    :scroll-to-error="false"
    @submit="onSubmit"
  >
    <h2 :id :class="brandClasses.heading">
      {{ $t.reservationTitle }}
    </h2>
    <p :class="brandClasses.requiredField">
      {{ $t.requiredField }}
    </p>
    <div :class="brandClasses.inputsWrapper">
      <vf-form-field
        v-slot="{ attrs }"
        name="firstName"
        :rule="validateRequired(separateNameFields ? $t.firstName : $t.name)"
      >
        <vf-input v-model="form.firstName" v-bind="attrs" required>
          {{ separateNameFields ? $t.firstName : $t.name }}
        </vf-input>
      </vf-form-field>
      <vf-form-field
        v-slot="{ attrs }"
        v-if="separateNameFields"
        name="lastName"
        :rule="validateRequired($t.lastName)"
      >
        <vf-input v-model="form.lastName" v-bind="attrs" required>
          {{ $t.lastName }}
        </vf-input>
      </vf-form-field>
      <vf-form-field v-slot="{ attrs }" name="email" :rule="[validateRequired($t.email), validateEmail()]">
        <vf-input v-model="form.email" v-bind="attrs" required type="email">
          {{ $t.email }}
        </vf-input>
      </vf-form-field>
      <vf-form-field
        v-slot="{ attrs }"
        name="phone"
        :rule="[validateRequired($t.mobileNumber), validatePhone(form.phoneCode)]"
      >
        <input-phone v-model="form.phone" v-model:code="form.phoneCode" v-bind="attrs" required show-country-code>
          {{ $t.mobileNumber }}
        </input-phone>
      </vf-form-field>
    </div>
    <vf-form-field v-slot="{ attrs }" name="robisTerms" :rule="validateRequired('', $t.validators.requiredCheckbox)">
      <vf-checkbox v-model="form.robisTerms" v-bind="attrs" :size="checkboxSize">
        <form-legal-disclaimer :content="$t.reservationPolicy" />
      </vf-checkbox>
    </vf-form-field>
    <vf-button :class="brandClasses.cta" :loading type="submit">
      {{ $t.submit }}
    </vf-button>
  </base-form>
</template>

<script lang="ts" setup>
import type { StoreShippingAddress } from '#types/cart'

const { cartId, storeShippingAddress } = defineProps<{
  cartId: string
  storeShippingAddress: StoreShippingAddress
}>()

const emit = defineEmits<{
  success: []
}>()

const { cart } = useApi()
const { brandClasses, checkboxSize, separateNameFields } = useAppConfig().components.form.robis
const countryCode = useCountryCode()
const id = useId()
const { validateEmail, validateRequired } = useLocalizedValidators()
const { $t } = useNuxtApp()
const toast = useToaster()

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  phoneCode: getPhoneCodeByCountryCode(countryCode) || '',
  robisTerms: false
})

const loading = ref(false)

const onSubmit = async () => {
  loading.value = true

  try {
    const address = {
      ...form,
      firstName: (separateNameFields ? form.firstName : form.firstName.split(' ')[0]) || '',
      lastName: (separateNameFields ? form.lastName : form.firstName.split(' ').slice(1).join(' ')) || '',
      phone: form.phone
        ? formatE164phone(
            form.phone,
            form.phoneCode
          )
        : '',
      city: storeShippingAddress.city,
      addressLine1: storeShippingAddress.addressLine1,
      postalCode: storeShippingAddress.postalCode,
      country: storeShippingAddress.country
    }

    await cart.$placeOrder({
      recaptcha_response: '',
      cartId,
      billingAddress: {
        ...storeShippingAddress,
        firstName: address.firstName,
        lastName: address.lastName,
        phone: address.phone,
        email: address.email,
        countryCode: address.country,
        subscriptions: {
          newsletterConsent: false
        }
      },
      paymentMethod: {
        code: 'PAY_IN_STORE',
        additionalData: []
      }
    }, { context: 'robis' })

    emit('success')

    closeDialogs()

    toast.add({
      autoClose: false,
      props: {
        type: 'success',
        title: $t.reservationSuccess.toastTitle,
        message: $t.reservationSuccess.toastMessage
      }
    })
  }
  catch {
    closeDialogs()

    toast.add({
      autoClose: false,
      props: {
        type: 'error',
        message: $t.reservationError
      }
    })
  }
  finally {
    loading.value = false
  }
}
</script>
